import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';

function generatePassword(length = 8) {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint8Array(length);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  let result = '';
  for (let i = 0; i < length; i += 1) {
    result += alphabet[bytes[i] % alphabet.length];
  }
  return result;
}

/**
 * Confirm turning blålys override on or off by repeating a generated password.
 */
export default function BlaalysConfirmDialog({
  open,
  enabling,
  submitting,
  error,
  onCancel,
  onConfirm
}) {
  const password = useMemo(() => (open ? generatePassword(8) : ''), [open]);
  const [confirmValue, setConfirmValue] = useState('');
  const [copied, setCopied] = useState(false);
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (open) {
      setConfirmValue('');
      setCopied(false);
      setLocalError(null);
    }
  }, [open, password]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setLocalError(null);
    } catch {
      setLocalError('Kunne ikke kopiere. Marker passordet og kopier manuelt.');
    }
  };

  const handleConfirm = () => {
    if (confirmValue.trim() !== password) {
      setLocalError('Passordet stemmer ikke. Kopier og lim inn det genererte passordet.');
      return;
    }
    setLocalError(null);
    onConfirm();
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {enabling ? 'Slå på blålysmodus' : 'Slå av blålysmodus'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {enabling
            ? 'Når blålysmodus er på, kan påloggede blålystelefoner åpne låser uten felles tilgangsgruppe.'
            : 'Når blålysmodus er av, må blålystelefoner ha tilgangsgruppe som vanlig.'}
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          Generert passord
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={password}
            InputProps={{ readOnly: true }}
            inputProps={{ 'aria-label': 'Generert passord', style: { fontFamily: 'monospace', letterSpacing: 2 } }}
          />
          <IconButton aria-label="Kopier passord" onClick={handleCopy} disabled={!password}>
            <ContentCopy />
          </IconButton>
        </Box>
        {copied && (
          <Typography variant="caption" color="success.main" display="block" sx={{ mb: 1 }}>
            Kopiert til utklippstavlen
          </Typography>
        )}
        <TextField
          fullWidth
          label="Gjenta passordet"
          value={confirmValue}
          onChange={(event) => setConfirmValue(event.target.value)}
          disabled={submitting}
          autoComplete="off"
          inputProps={{ 'aria-label': 'Gjenta passordet', style: { fontFamily: 'monospace' } }}
        />
        {(localError || error) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {localError || error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          Avbryt
        </Button>
        <Button
          variant="contained"
          color={enabling ? 'error' : 'primary'}
          onClick={handleConfirm}
          disabled={submitting || !confirmValue.trim()}
        >
          {submitting ? 'Lagrer…' : enabling ? 'Slå på' : 'Slå av'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
